import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import StatusBadge from './StatusBadge';
import NewClientModal from '../../../components/ui/NewClientModal';

const OrderModal = ({ order, isOpen, onClose, onSave, mode = 'view', userRole = 'admin' }) => {
  const [formData, setFormData] = useState({
    customer: '',
    phone: '',
    vehicle: '',
    plates: '',
    status: 'cotizada',
    technician: '',
    items: [],
    discount: 0,
    notes: ''
  });

  const [newItem, setNewItem] = useState({
    type: 'service',
    name: '',
    quantity: 1,
    price: 0
  });

  const [isNewClientModalOpen, setIsNewClientModalOpen] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([
    { value: 'juan_perez', label: 'Juan Pérez - 612-345-6789' },
    { value: 'maria_garcia', label: 'María García - 612-987-6543' },
    { value: 'carlos_lopez', label: 'Carlos López - 612-456-7890' },
    { value: 'ana_martinez', label: 'Ana Martínez - 612-234-5678' }
  ]);

  useEffect(() => {
    if (order && isOpen) {
      setFormData({
        customer: order?.customer || '',
        phone: order?.phone || '',
        vehicle: order?.vehicle || '',
        plates: order?.plates || '',
        status: order?.status || 'cotizada',
        technician: order?.technician || '',
        items: order?.items || [],
        discount: order?.discount || 0,
        notes: order?.notes || ''
      });
    }
  }, [order, isOpen]);

  if (!isOpen) return null;

  const vehicleOptions = [
    { value: 'toyota_corolla', label: 'Toyota Corolla 2020 - ABC-123' },
    { value: 'honda_civic', label: 'Honda Civic 2019 - XYZ-789' },
    { value: 'nissan_versa', label: 'Nissan Versa 2021 - DEF-456' },
    { value: 'mazda_3', label: 'Mazda 3 2022 - GHI-012' }
  ];

  const technicianOptions = [
    { value: 'miguel_sanchez', label: 'Miguel Sánchez' },
    { value: 'roberto_diaz', label: 'Roberto Díaz' },
    { value: 'fernando_ruiz', label: 'Fernando Ruiz' }
  ];

  const statusOptions = [
    { value: 'cotizada', label: 'Cotizada' },
    { value: 'aprobada', label: 'Aprobada' },
    { value: 'en_proceso', label: 'En proceso' },
    { value: 'lista', label: 'Lista' },
    { value: 'entregada', label: 'Entregada' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const serviceOptions = [
    { value: 'cambio_aceite', label: 'Cambio de aceite - €45.00' },
    { value: 'alineacion', label: 'Alineación - €60.00' },
    { value: 'balanceo', label: 'Balanceo - €40.00' },
    { value: 'frenos', label: 'Revisión de frenos - €80.00' }
  ];

  const productOptions = [
    { value: 'llanta_185_65_r15', label: 'Llanta 185/65 R15 - €85.00' },
    { value: 'llanta_195_55_r16', label: 'Llanta 195/55 R16 - €95.00' },
    { value: 'aceite_5w30', label: 'Aceite 5W-30 - €35.00' },
    { value: 'filtro_aire', label: 'Filtro de aire - €15.00' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNewClientSave = (newClient) => {
    setCustomerOptions([...customerOptions, newClient]);
    setFormData({ ...formData, customer: newClient?.value, phone: newClient?.phone });
    setIsNewClientModalOpen(false);
  };

  const handleAddItem = () => {
    if (newItem?.name && newItem?.quantity > 0 && newItem?.price > 0) {
      setFormData({
        ...formData,
        items: [...formData?.items, { ...newItem, id: Date.now() }]
      });
      setNewItem({ type: 'service', name: '', quantity: 1, price: 0 });
    }
  };

  const handleRemoveItem = (itemId) => {
    setFormData({
      ...formData,
      items: formData?.items?.filter(item => item?.id !== itemId)
    });
  };

  const calculateSubtotal = () => {
    return formData?.items?.reduce((sum, item) => sum + (item?.quantity * item?.price), 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    return subtotal - formData?.discount;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const handleSave = () => {
    onSave({
      ...order,
      ...formData,
      total: calculateTotal()
    });
  };

  const isReadOnly = mode === 'view';
  const canEditDiscount = userRole === 'admin';

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              {mode === 'create' ? 'Nueva Orden' : mode === 'edit' ? 'Editar Orden' : 'Detalle de Orden'}
            </h2>
            {order && <StatusBadge status={formData?.status} />}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth focus-ring"
            aria-label="Cerrar"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-custom p-4 md:p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Cliente"
                  options={customerOptions}
                  value={formData?.customer}
                  onChange={(value) => handleInputChange('customer', value)}
                  disabled={isReadOnly}
                  required
                  searchable
                />
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => setIsNewClientModalOpen(true)}
                    className="mt-2 flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-smooth"
                  >
                    <Icon name="Plus" size={16} />
                    Nuevo Cliente
                  </button>
                )}
              </div>

              <Input
                label="Teléfono"
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                disabled={isReadOnly}
                required
              />

              <Select
                label="Vehículo"
                options={vehicleOptions}
                value={formData?.vehicle}
                onChange={(value) => handleInputChange('vehicle', value)}
                disabled={isReadOnly}
                required
                searchable
              />

              <Input
                label="Placas"
                type="text"
                value={formData?.plates}
                onChange={(e) => handleInputChange('plates', e?.target?.value)}
                disabled={isReadOnly}
                required
              />

              <Select
                label="Estado"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange('status', value)}
                disabled={isReadOnly}
              />

              <Select
                label="Técnico asignado"
                options={technicianOptions}
                value={formData?.technician}
                onChange={(value) => handleInputChange('technician', value)}
                disabled={isReadOnly}
              />
            </div>

            {!isReadOnly && (
              <div className="bg-muted rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4">Agregar artículo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <Select
                    label="Tipo"
                    options={[
                      { value: 'service', label: 'Servicio' },
                      { value: 'product', label: 'Producto' }
                    ]}
                    value={newItem?.type}
                    onChange={(value) => setNewItem({ ...newItem, type: value })}
                  />

                  <Select
                    label={newItem?.type === 'service' ? 'Servicio' : 'Producto'}
                    options={newItem?.type === 'service' ? serviceOptions : productOptions}
                    value={newItem?.name}
                    onChange={(value) => setNewItem({ ...newItem, name: value })}
                    searchable
                  />

                  <Input
                    label="Cantidad"
                    type="number"
                    min="1"
                    value={newItem?.quantity}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e?.target?.value) || 1 })}
                  />

                  <Input
                    label="Precio"
                    type="number"
                    min="0"
                    step="0.01"
                    value={newItem?.price}
                    onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e?.target?.value) || 0 })}
                  />

                  <div className="flex items-end">
                    <Button
                      variant="default"
                      onClick={handleAddItem}
                      iconName="Plus"
                      fullWidth
                    >
                      Agregar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Artículos</h3>
              {formData?.items?.length === 0 ? (
                <div className="text-center py-8 bg-muted rounded-lg">
                  <Icon name="Package" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No hay artículos agregados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formData?.items?.map((item) => (
                    <div key={item?.id} className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{item?.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {item?.quantity} × {formatCurrency(item?.price)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-foreground whitespace-nowrap">
                          {formatCurrency(item?.quantity * item?.price)}
                        </p>
                      </div>
                      {!isReadOnly && (
                        <button
                          onClick={() => handleRemoveItem(item?.id)}
                          className="p-2 rounded-lg hover:bg-destructive/10 text-destructive transition-smooth focus-ring"
                          aria-label="Eliminar artículo"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(calculateSubtotal())}
                </span>
              </div>

              {canEditDiscount && !isReadOnly && (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">Descuento</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData?.discount}
                    onChange={(e) => handleInputChange('discount', parseFloat(e?.target?.value) || 0)}
                    className="max-w-32"
                  />
                </div>
              )}

              {formData?.discount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Descuento</span>
                  <span className="text-sm font-medium text-destructive">
                    -{formatCurrency(formData?.discount)}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <span className="text-base font-semibold text-foreground">Total</span>
                <span className="text-lg font-bold text-foreground">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>
            </div>

            <Input
              label="Notas adicionales"
              type="text"
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              disabled={isReadOnly}
              description="Información adicional sobre la orden"
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            {isReadOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!isReadOnly && (
            <Button
              variant="default"
              onClick={handleSave}
              iconName="Save"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              Guardar
            </Button>
          )}
        </div>
      </div>

      <NewClientModal
        isOpen={isNewClientModalOpen}
        onClose={() => setIsNewClientModalOpen(false)}
        onSave={handleNewClientSave}
      />
    </div>
  );
};

export default OrderModal;